import { Component, Input, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NotesComponent } from '../life-style/notes/notes.component';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts';
import { CommonModule, formatDate } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MemberDashboardService } from '../member-dashboard.service';
import { SleepDataList } from '../../models/sleep-data';
import moment from 'moment';

@Component({
  selector: 'app-sleep',
  standalone: true,
  imports: [NotesComponent, HighchartsChartModule, CommonModule, FormsModule, MatFormFieldModule, MatDatepickerModule, MatProgressSpinnerModule],
  templateUrl: './sleep.component.html',
  styleUrl: './sleep.component.css'
})

export class SleepComponent {
  @Input() sleep: any = null;
  @Input() sDate: any;
  @Input() eDate: any;
  @ViewChild('chart1') chart1: any;
  @ViewChild('chart2') chart2: any;

  // Sleep Graph properties
  memberId: any;
  dates: string[] = [];
  rawDates: string[] = [];
  endDate: string = new Date().toISOString().split('T')[0];
  startDate: string = new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0];
  currentDateBmi: any;
  previousDateBmi: any;
  remSleepPerctGraph: number[] = [];
  deepSleepPerctGraph: number[] = [];
  lightSleepPerctGraph: number[] = [];
  awakePerctGraph: number[] = [];
  totalSleepDurationPerct: number[] = [];
  remSleepHrs: number[] = [];
  deepSleepHrs: number[] = [];
  lightSleepHrs: number[] = [];
  awakeHrs: number[] = [];
  totalSleepDurationHrs: (number | null)[] = [];
  totalSleepDurationText: string[] = [];
  startTimeText: string[] = [];
  endTimeText: string[] = [];
  deepSleepTimeGraph: any;
  remSleepTimeGraph: any;
  lightSleepTimeGraph: any;
  awakeTimeGraph: any;
  chartOptions1: Highcharts.Options = {};
  chartOptions2: Highcharts.Options = {};
  private chartInstance1: any;
  private chartInstance2: any;
  updateFlag: boolean = false;
  oneToOneFlag: boolean = true;
  runOutsideAngular: boolean = false;
  showChart1: boolean = true;
  showChart2: boolean = true;
  isLoading: number = 1;
  noDataFound: boolean = false;
  hasSleepStageData: boolean = false; // true when response.result has sleep stage data
  // New API shape provides goal/duration timeline under "Total Sleep".days
  sleepGoals: any[] = [];
  totalSleepDays: any[] = [];
  filteredTotalSleepDays: any[] = [];

  // Original sleep component properties
  remSleepPerct: any = 0;
  deepSleepPerct: any = 0;
  lightSleepPerct: any = 0;
  awakePerct: any = 0;
  totalSleepHrs: any = '---';
  totalSleepGoal: any = '---';
  comparedHrs: any = '---';
  comparedImg: any = "/assets/img/flat.png";
  deepSleepTime: any = '---';
  remSleepTime: any = '---';
  lightSleepTime: any = '---';
  awakeTime: any = '---';
  deepSleepStatus: any = '';
  remSleepStatus: any = '';
  lightSleepStatus: any = '';
  awakeStatus: any = '';
  deepStatusColor: any = 'white';
  remStatusColor: any = 'white';
  lightStatusColor: any = 'white';
  awakeStatusColor: any = 'white';
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};

  constructor(
    private dashboardService: MemberDashboardService,
    private route: ActivatedRoute
  ) {
    var d = new Date();
    var p = new Date(new Date().setDate(new Date().getDate() - 7));
    this.currentDateBmi = d
    this.previousDateBmi = p;
  }

  ngOnInit(): void {
    this.memberId = this.route.snapshot.queryParamMap.get('memberId') || '';
    this.isLoading = 1;

    // Initialize the bar chart with default values
    this.updateSleepBarChart();

    // Use input dates from parent if provided, otherwise use default dates
    const startDateToUse = this.sDate || this.startDate;
    const endDateToUse = this.eDate || this.endDate;

    // Update internal date properties
    this.startDate = startDateToUse;
    this.endDate = endDateToUse;

    // Fetch sleep graph data
    if (this.memberId) {
      this.getSleepGraphData();
      this.getSleepData(startDateToUse, endDateToUse);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update the bar chart when sleep data changes
    if (changes['sleep']) {
      this.updateSleepBarChart();
    }

    // React to changes in sDate or eDate inputs from parent
    if ((changes['sDate'] || changes['eDate']) && !changes['sDate']?.firstChange && !changes['eDate']?.firstChange) {
      const startDateToUse = this.sDate || this.startDate;
      const endDateToUse = this.eDate || this.endDate;

      // Update internal date properties
      this.startDate = startDateToUse;
      this.endDate = endDateToUse;

      this.isLoading = 1;
      if (this.memberId) {
        this.getSleepGraphData();
        this.getSleepData(startDateToUse, endDateToUse);
      }
    }
  }

  updateSleepBarChart(): void {
    this.remSleepPerct = 0;
    this.deepSleepPerct = 0;
    this.lightSleepPerct = 0;
    this.awakePerct = 0;
    this.totalSleepHrs = '---';
    this.totalSleepGoal = '---';
    this.comparedHrs = '---';
    this.comparedImg = "/assets/img/flat.png";
    this.deepSleepTime = '---';
    this.remSleepTime = '---';
    this.lightSleepTime = '---';
    this.awakeTime = '---';
    this.deepSleepStatus = '';
    this.remSleepStatus = '';
    this.lightSleepStatus = '';
    this.awakeStatus = '';
    this.deepStatusColor = 'white';
    this.remStatusColor = 'white';
    this.lightStatusColor = 'white';
    this.awakeStatusColor = 'white';

    if (this.sleep && this.sleep.hasSleepsData) {
      this.remSleepPerct = parseFloat((this.sleep.totalRemSleepDuration / this.sleep.sleepDurationForAvg * 100).toFixed(1));
      this.deepSleepPerct = parseFloat((this.sleep.totalDeepSleepDuration / this.sleep.sleepDurationForAvg * 100).toFixed(1));
      this.lightSleepPerct = parseFloat((this.sleep.totalLightSleepDuration / this.sleep.sleepDurationForAvg * 100).toFixed(1));
      this.awakePerct = parseFloat((this.sleep.totalAwakeDuration / this.sleep.sleepDurationForAvg * 100).toFixed(1));
      this.deepSleepTime = this.getHrsMints(this.sleep.totalDeepSleepDuration);
      this.deepSleepStatus = this.sleep.deepSleepStatus;
      this.deepStatusColor = this.getColor(this.deepSleepStatus);
      this.remSleepTime = this.getHrsMints(this.sleep.totalRemSleepDuration);
      this.remSleepStatus = this.sleep.remSleepStatus;
      this.remStatusColor = this.getColor(this.remSleepStatus);
      this.lightSleepTime = this.getHrsMints(this.sleep.totalLightSleepDuration);
      this.lightSleepStatus = this.sleep.lightSleepStatus;
      this.lightStatusColor = this.getColor(this.lightSleepStatus);
      this.awakeTime = this.getHrsMints(this.sleep.totalAwakeDuration);
      this.awakeStatus = this.sleep.awakeStatus;
      this.awakeStatusColor = this.getColor(this.awakeStatus);
    }
    if (this.sleep && this.sleep.totalSleepMsg) {
      this.totalSleepHrs = this.getHrsMints(this.sleep.totalSleepDuration);
      this.totalSleepGoal = this.getHrsMints(this.sleep.totalSleepDurationGoal);
      if (this.sleep.diffFromPrevPeriod > 0) {
        this.comparedHrs = '+ ' + this.getHrsMints(this.sleep.diffFromPrevPeriod);
        this.comparedImg = "/assets/img/up1.png";
      } else if (this.sleep.diffFromPrevPeriod < 0) {
        this.comparedHrs = '- ' + this.getHrsMints(Math.abs(this.sleep.diffFromPrevPeriod));
        this.comparedImg = "/assets/img/down.png";
      } else if (this.sleep.diffFromPrevPeriod == 0) {
        this.comparedHrs = '0 Hrs';
      }
    }
    this.chartOptions = {
      chart: {
        backgroundColor: 'white',
        type: 'bar'
      },
      title: {
        text: ''
      },
      xAxis: {
        visible: false,
        categories: ['']
      },
      yAxis: {
        visible: false,
        title: {
          text: ''
        }
      },

      credits: {
        enabled: false
      },
      tooltip: {
        formatter: function () {
          if (this.y === 0) {
            return false;
          }
          return this.series.name + ' : ' + this.y + ' %';
        },
        backgroundColor: 'rgba(0,0,0, 0.6)',
        borderWidth: 1,
        shadow: true,
        style: {
          color: '#fff',
          fontSize: '10pt',
          fontFamily: 'Urbanist, sans-serif'
        }
      },

      plotOptions: {

        series: {
          stacking: 'normal',
          dataLabels: {
            enabled: false
          }
        }
      },
      series: [{
        type: 'bar',
        name: 'Deep',
        data: [this.deepSleepPerct],
        color: '#66CC66'
      }, {
        type: 'bar',
        name: 'REM',
        data: [this.remSleepPerct],
        color: '#21A4B6'
      }, {
        type: 'bar',
        name: 'Light',
        data: [this.lightSleepPerct],
        color: '#4787F6'
      }, {
        type: 'bar',
        name: 'Awake',
        data: [this.awakePerct],
        color: '#FFDA48'
      }]
    };
  }
  getColor(status: any) {
    if (status == 'Excellent') {
      return '#20AA4A';
    }
    else if (status == 'Good') {
      return '#D0AF00';
    }
    else if (status == 'Needs Attention') {
      return '#FF3939';
    }
  }
  getHrsMints(seconds: any) {

    let hours = Math.floor(seconds / 3600);
    let minutes = Math.floor((seconds % 3600) / 60);
    return hours + ' hrs ' + minutes + ' mins';
  }

  // Sleep Graph methods
  getSleepGraphData() {
    this.dashboardService.getGraphSleepData(this.memberId, this.startDate, this.endDate).subscribe((response: any) => {
      // New API: "Total Sleep" can be array directly or object with .days
      this.sleepGoals = response.sleepGoals || [];
      const totalSleepRaw = response?.['Total Sleep'];
      this.totalSleepDays = Array.isArray(totalSleepRaw) ? totalSleepRaw : (totalSleepRaw?.days || []);
      
      // Filter dates to only include those within date picker range
      const datePickerStart = moment(this.startDate).startOf('day');
      const datePickerEnd = moment(this.endDate).startOf('day');
      
      // Build filtered view of total sleep days limited to current date picker range
      this.filteredTotalSleepDays = (this.totalSleepDays || []).filter((day: any) => {
        const dayDate = moment(day?.date).startOf('day');
        return dayDate.isValid() && dayDate.isSameOrAfter(datePickerStart) && dayDate.isSameOrBefore(datePickerEnd);
      });

      // Collect all dates from sleep duration data
      const sleepDataDates = new Set<string>();
      const sleepDataMap = new Map<string, any>();
      
      const filteredResults = response.result.filter((item: any) => {
        const itemDate = moment(item.dateKey).startOf('day');
        return itemDate.isSameOrAfter(datePickerStart) && itemDate.isSameOrBefore(datePickerEnd);
      });
      
      filteredResults.forEach((item: any) => {
        const dateKey = moment(item.dateKey).format('YYYY-MM-DD');
        sleepDataDates.add(dateKey);
        sleepDataMap.set(dateKey, item);
      });
      
      // Sleep Stage chart: use only dates that have sleep stage data (response.result). No merge with Total Sleep / goal dates.
      const allDatesArray = Array.from(sleepDataDates).sort((a, b) => {
        return moment(a).valueOf() - moment(b).valueOf();
      });
      
      // Check if no data found
      this.noDataFound = sleepDataDates.size === 0;
      this.hasSleepStageData = sleepDataDates.size > 0;
      const hasPlottableTotalSleepData = (this.filteredTotalSleepDays || []).some((day: any) =>
        (day?.goal != null && Number.isFinite(Number(day.goal))) ||
        (day?.data != null && day?.data?.duration != null && day?.data?.endTime)
      );

      // Show empty charts with message when result is empty and no plottable goal/duration
      // Force chart recreation (like data-to-data case) to reset zoom and clear stale axis/state
      if (this.noDataFound && !hasPlottableTotalSleepData) {
        this.chartInstance1 = null;
        this.chartInstance2 = null;
        this.createEmptyChart();
        this.forceChartRecreation();
        this.isLoading = 2;
        return;
      }
      
      // Build dates arrays aligned with merged dates
      this.rawDates = allDatesArray;
      this.dates = allDatesArray.map((dateStr: string) => {
        return moment(dateStr).format('ddd,<br/>MMM D');
      });
      
      // Build sleep data arrays aligned with merged dates
      // Use actual data when available, null/0/empty otherwise
      this.deepSleepPerctGraph = allDatesArray.map((dateStr: string) => {
        const item = sleepDataMap.get(dateStr);
        return item ? Number(Math.round(item.totalDeepSleepDuration / item.totalSleepDuration * 100)) : 0;
      }) as number[];
      
      this.lightSleepPerctGraph = allDatesArray.map((dateStr: string) => {
        const item = sleepDataMap.get(dateStr);
        return item ? Number(Math.round(item.totalLightSleepDuration / item.totalSleepDuration * 100)) : 0;
      }) as number[];
      
      this.awakePerctGraph = allDatesArray.map((dateStr: string) => {
        const item = sleepDataMap.get(dateStr);
        return item ? Number(Math.round(item.totalAwakeDuration / item.totalSleepDuration * 100)) : 0;
      }) as number[];
      
      this.remSleepPerctGraph = allDatesArray.map((dateStr: string) => {
        const item = sleepDataMap.get(dateStr);
        return item ? Number(Math.round(item.totalRemSleepDuration / item.totalSleepDuration * 100)) : 0;
      }) as number[];
      
      this.totalSleepDurationPerct = allDatesArray.map((dateStr: string) => {
        const item = sleepDataMap.get(dateStr);
        return item ? Number(Math.round((item.totalSleepDuration / (24 * 60 * 60)) * 100).toFixed(2)) : 0;
      }) as number[];

      this.totalSleepDurationHrs = allDatesArray.map((dateStr: string) => {
        const item = sleepDataMap.get(dateStr);
        return item ? Number(this.convertSecondsToDecimalHours(item.totalSleepDuration)) : null;
      }) as (number | null)[];

      this.totalSleepDurationText = allDatesArray.map((dateStr: string) => {
        const item = sleepDataMap.get(dateStr);
        return item ? this.getHrsMints(item.totalSleepDuration) : '';
      }) as string[];
      
      this.startTimeText = allDatesArray.map((dateStr: string) => {
        const item = sleepDataMap.get(dateStr);
        return item ? item.sleepStartTime : '';
      }) as string[];
      
      this.endTimeText = allDatesArray.map((dateStr: string) => {
        const item = sleepDataMap.get(dateStr);
        return item ? item.sleepEndTime : '';
      }) as string[];

      this.deepSleepTimeGraph = allDatesArray.map((dateStr: string) => {
        const item = sleepDataMap.get(dateStr);
        return item ? this.getHrsMints(item.totalDeepSleepDuration) : '';
      }) as string[];
      
      this.remSleepTimeGraph = allDatesArray.map((dateStr: string) => {
        const item = sleepDataMap.get(dateStr);
        return item ? this.getHrsMints(item.totalRemSleepDuration) : '';
      }) as string[];
      
      this.lightSleepTimeGraph = allDatesArray.map((dateStr: string) => {
        const item = sleepDataMap.get(dateStr);
        return item ? this.getHrsMints(item.totalLightSleepDuration) : '';
      }) as string[];
      
      this.awakeTimeGraph = allDatesArray.map((dateStr: string) => {
        const item = sleepDataMap.get(dateStr);
        return item ? this.getHrsMints(item.totalAwakeDuration) : '';
      }) as string[];

      // Reset chart instances to ensure clean recreation
      this.chartInstance1 = null;
      this.chartInstance2 = null;

      this.forceChartRecreation();

      setTimeout(() => {
        if (this.hasSleepStageData) {
          this.createSleepStageChart();
        } else {
          // Render the predefined empty chart state for the Sleep Stage graph
          this.createEmptyChart();
        }
        this.createTotalSleepChart();
        this.isLoading = 2;
      }, 150);
    });
  }

  /** Sleep Stage Percentage graph: uses only response.result (sleep stage data). Independent of Total Sleep chart. */
  private createSleepStageChart(): void {
    const percentageMaxValue = this.getMaxValueFromPercentageData();
    const hasPercentageData = percentageMaxValue !== undefined;
    const totalPoints = this.dates.length;
    const isLongRange = totalPoints > 15;
    const xLabelStep = isLongRange ? Math.ceil(totalPoints / 12) : 1;

    this.chartOptions2 = {
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line',
        panKey: 'shift',
        panning: {
          enabled: true,
          type: 'x'
        },
        zooming: {
          type: 'x'
        }
      },
      title: {
        text: 'Sleep Stage Percentages'
      },
      subtitle: {
        text: this.hasSleepStageData ? '' : 'No data recorded for the selected date range',
        align: 'center',
        verticalAlign: 'middle',
        style: { color: '#888', fontSize: '12px' }
      },
      xAxis: {
        type: 'datetime',
        categories: this.hasSleepStageData ? this.dates : [],
        lineWidth: 2,
        labels: {
          useHTML: true,
          step: xLabelStep
        }
      },
      yAxis: {
        min: 0,
        max: this.hasSleepStageData ? percentageMaxValue : 100,
        lineWidth: 2,
        gridLineWidth: (this.hasSleepStageData && hasPercentageData) ? 1 : 0,
        minorGridLineWidth: 0,
        labels: { enabled: this.hasSleepStageData && hasPercentageData },
        tickWidth: (this.hasSleepStageData && hasPercentageData) ? undefined : 0,
        tickLength: (this.hasSleepStageData && hasPercentageData) ? undefined : 0,
        title: {
          text: 'Percentage for a day'
        }
      },
      credits: {
        enabled: false
      },
      tooltip: {
        enabled: true,
        useHTML: true,
        formatter: function () {
          const customData = this.point.options.custom ?? {};
          if (this.y === 0) {
            return false;
          }
          const dateStr = customData['dateStr'] || '';
          return `
      <div>
        <span style="font-size: 12px; color: #fefefe;"><b>${dateStr}</b></span><br>
        <span style="font-size: 12px; color: #fefefe;"><b>${this.series.name}</b></span><br>
        <span style="font-size: 12px; color: #fefefe;">Percentage: ${this.y}%</span><br>
        <span style="font-size: 12px; color: #fefefe;">Duration: <b>${customData['timeHrs'] || 'N/A'}</b></span>
        
      </div>`;

        },
        backgroundColor: 'rgba(0,0,0, 0.6)',
        borderWidth: 1,
        shadow: true,
        style: {
          color: '#fff',
          fontSize: '10pt',
          fontFamily: 'Urbanist, sans-serif'
        }
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: false
          },
          enableMouseTracking: true
        },
        series: {
          marker: {
            symbol: 'circle',
            enabled: true,
            radius: 3,
            lineWidth: 1,
            states: {
              hover: {
                enabled: true,
                radius: 5
              }
            }
          }
        }
      },
      series: this.hasSleepStageData
        ? [{
            type: 'line',
            name: 'Deep Sleep',
            data: this.deepSleepPerctGraph.map((value: number, index: number) => ({
              x: index,
              y: value,
              custom: {
                timeHrs: this.deepSleepTimeGraph[index],
                dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
              }
            })) as Highcharts.PointOptionsObject[],
            color: '#66CC66'
          }, {
            type: 'line',
            name: 'REM Sleep',
            data: this.remSleepPerctGraph.map((value: number, index: number) => ({
              x: index,
              y: value,
              custom: {
                timeHrs: this.remSleepTimeGraph[index],
                dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
              }
            })) as Highcharts.PointOptionsObject[],
            color: '#21A4B6'
          }, {
            type: 'line',
            name: 'Light Sleep',
            data: this.lightSleepPerctGraph.map((value: number, index: number) => ({
              x: index,
              y: value,
              custom: {
                timeHrs: this.lightSleepTimeGraph[index],
                dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
              }
            })) as Highcharts.PointOptionsObject[],
            color: '#4787F6'
          }, {
            type: 'line',
            name: 'Awake Time',
            data: this.awakePerctGraph.map((value: number, index: number) => ({
              x: index,
              y: value,
              custom: {
                timeHrs: this.awakeTimeGraph[index],
                dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
              }
            })) as Highcharts.PointOptionsObject[],
            color: '#FFDA48'
          }]
        : []
    };

    this.chartOptions2.chart!.events = {
      load: this.chartCallback2
    };
    this.updateFlag = true;
  }

  /** Total Sleep Time graph: uses only API "Total Sleep" array. Independent of Sleep Stage chart. X-axis: date + time (like metrics). */
  private createTotalSleepChart(): void {
    const goalPointsForRange = this.buildGoalPointsFromApiDays();
    const totalPointsForRange = this.buildTotalSleepPointsFromApiDays();
    const hasHoursData = totalPointsForRange.length > 0;
    const hasGoalData = goalPointsForRange.length > 0;
    const sleepPlotBands: any[] = [];
    let minTimestamp: number | undefined;
    let maxTimestamp: number | undefined;
    // Include goal/total points in x-axis range calculation
    [...goalPointsForRange, ...totalPointsForRange].forEach((p: any) => {
      const x = p?.x;
      if (typeof x !== 'number') return;
      if (minTimestamp === undefined || x < minTimestamp) minTimestamp = x;
      if (maxTimestamp === undefined || x > maxTimestamp) maxTimestamp = x;
    });

    if (hasHoursData) {
      // Build sleep plot bands (background ranges) only when there is plottable duration data
      (this.filteredTotalSleepDays || []).forEach((day: any) => {
        const data = day?.data;
        if (!data?.startTime || !data?.endTime) return;

        const from = moment(data.startTime).valueOf();
        const to = moment(data.endTime).valueOf();
        if (isNaN(from) || isNaN(to) || to <= from) return;

        // Color: #DE30FF at 10% opacity
        sleepPlotBands.push({
          from,
          to,
          color: 'rgba(222, 48, 255, 0.1)',
          zIndex: 0
        });

        // Also include these exact times in x-range considerations
        if (minTimestamp === undefined || from < minTimestamp) minTimestamp = from;
        if (maxTimestamp === undefined || to > maxTimestamp) maxTimestamp = to;
      });
    }

    // Fallback to selected date range when there are no points
    if (minTimestamp === undefined || maxTimestamp === undefined) {
      if (this.startDate && this.endDate) {
        minTimestamp = moment(this.startDate).startOf('day').valueOf();
        maxTimestamp = moment(this.endDate).endOf('day').valueOf();
      } else {
        minTimestamp = moment().subtract(7, 'days').startOf('day').valueOf();
        maxTimestamp = moment().endOf('day').valueOf();
      }
    }

    const dataRange = maxTimestamp - minTimestamp;
    if (dataRange < 24 * 60 * 60 * 1000) {
      // Single day or very small range – show full day
      minTimestamp = moment(minTimestamp).startOf('day').valueOf();
      maxTimestamp = moment(maxTimestamp).endOf('day').valueOf();
    } else {
      // Add 5% padding on both sides
      minTimestamp = minTimestamp - dataRange * 0.05;
      maxTimestamp = maxTimestamp + dataRange * 0.05;
    }

    this.chartOptions1 = {
      time: {
        useUTC: false
      },
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line',
        panKey: 'shift',
        panning: {
          enabled: true,
          type: 'x'
        },
        zooming: {
          type: 'x'
        }
      },
      title: {
        text: 'Total Sleep Time'
      },
      subtitle: {
        text: (goalPointsForRange.length > 0 || totalPointsForRange.length > 0) ? '' : 'No data recorded for the selected date range',
        align: 'center',
        verticalAlign: 'middle',
        style: { color: '#888', fontSize: '12px' }
      },
      xAxis: [
        {
          crosshair: true,
          lineWidth: 2,
          type: 'datetime',
          min: minTimestamp,
          max: maxTimestamp,
          dateTimeLabelFormats: {
            millisecond: '%H:%M:%S.%L',
            second: '%H:%M:%S',
            minute: '%H:%M',
            hour: '%H:%M',
            day: '%a,<br/>%b %e',
            week: '%a,<br/>%b %e',
            month: "%b '%y",
            year: '%Y',
          },
          tickPositioner: function (this: Highcharts.Axis): number[] {
            const min = this.min ?? 0;
            const max = this.max ?? 0;
            const range = max - min;
            const oneDay = 24 * 60 * 60 * 1000;
            const targetTicks = 12;
            if (range <= oneDay) {
              const step = range / (targetTicks - 1);
              const ticks: number[] = [];
              for (let i = 0; i < targetTicks; i++) {
                ticks.push(min + i * step);
              }
              return ticks;
            }
            const stepMs = Math.max(oneDay, Math.round(range / targetTicks / oneDay) * oneDay);
            const start = moment(min).startOf('day').valueOf();
            const ticks: number[] = [];
            let t = start;
            while (t <= max && ticks.length < targetTicks + 2) {
              if (t >= min) ticks.push(t);
              t += stepMs;
            }
            return ticks.length ? ticks : [min, max];
          },
          labels: {
            useHTML: true,
            formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
              const v = this.value;
              if (v == null || typeof v !== 'number') return '';
              const range = (this.axis?.max ?? 0) - (this.axis?.min ?? 0);
              const oneDay = 24 * 60 * 60 * 1000;
              if (range <= oneDay * 2) {
                const m = moment(v);
                const isStartOfDay = m.hours() === 0 && m.minutes() === 0;
                if (isStartOfDay) {
                  return m.format('ddd,') + '<br/>' + m.format('MMM D');
                }
                return m.format('HH:mm');
              }
              return moment(v).format('ddd,') + '<br/>' + moment(v).format('MMM D');
            },
          },
          plotBands: sleepPlotBands
        },
      ],
      yAxis: {
        min: 0,
        max: this.getMaxValueFromHoursSeriesData(),
        lineWidth: 2,
        tickAmount: (hasHoursData || hasGoalData) ? 7 : undefined,
        // Keep axis visible to show title; hide markings when no data
        labels: { enabled: (hasHoursData || hasGoalData) },
        tickWidth: (hasHoursData || hasGoalData) ? undefined : 0,
        tickLength: (hasHoursData || hasGoalData) ? undefined : 0,
        gridLineWidth: (hasHoursData || hasGoalData) ? 1 : 0,
        minorGridLineWidth: 0,
        title: {
          text: 'Hours'
        }
      },
      credits: {
        enabled: false
      },
      tooltip: {
        enabled: true,
        useHTML: true,
        formatter: function () {
          const customData = this.point.options.custom ?? {};
          const dateStr = customData['dateStr'] || '';

          // Special tooltip for Goal line - show only date and goal value (no start/end time)
          if (this.series.name === 'Sleep Goal' || this.series.name === 'Goal') {
            const seconds = (customData && customData['seconds']) ? (customData['seconds'] as number) : Math.round(((this.y || 0) as number) * 3600);
            const hrs = Math.floor(seconds / 3600);
            const mins = Math.floor((seconds % 3600) / 60);
            return `
      <div>
        <span style="font-size: 12px; color: #fefefe;"><b>${dateStr}</b></span><br>
        <span style="font-size: 12px; color: #fefefe;">Sleep Goal duration: <b>${hrs} hrs ${mins} mins</b></span>
      </div>
    `;
          }

          // Regular tooltip for other series
          const seconds = (customData && customData['seconds']) ? (customData['seconds'] as number) : Math.round(((this.y || 0) as number) * 3600);
          const hrs = Math.floor(seconds / 3600);
          const mins = Math.floor((seconds % 3600) / 60);
          return `
      <div>
        <span style="font-size: 12px; color: #fefefe;"><b>${dateStr}</b></span><br>
        <span style="font-size: 12px; color: #fefefe;"> Sleep Duration: <b>${hrs} hrs ${mins} mins</b></span>
        <br/>
         <span style="font-size: 12px; color: #fefefe;"> StartTime:   <b>${customData['info1'] || 'N/A'}</b></span>
         <br>
          <span style="font-size: 12px; color: #fefefe;">EndTime:    <b>${customData['info2'] || 'N/A'}</b></span>
      </div>
    `;
        },
        backgroundColor: 'rgba(0,0,0, 0.6)',
        borderWidth: 1,
        shadow: true,
        style: {
          color: '#fff',
          fontSize: '10pt',
          fontFamily: 'Urbanist, sans-serif'
        }
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: false
          },
          enableMouseTracking: true
        },
        series: {
          marker: {
            symbol: 'circle',
            enabled: true,
            radius: 3,
            lineWidth: 1,
            states: {
              hover: {
                enabled: true,
                radius: 5
              }
            }
          }
        }
      },
      series: (goalPointsForRange.length === 0 && totalPointsForRange.length === 0)
        ? []
        : [
            {
              type: 'line',
              name: 'Sleep Goal',
              data: goalPointsForRange,
              color: '#FFD700',
              lineWidth: 2,
              marker: {
                enabled: true,
                radius: 3,
                symbol: 'circle'
              }
            },
            {
              type: 'line',
              name: 'Total sleep',
              data: totalPointsForRange,
              color: '#66CC66',
              lineWidth: 2,
              marker: {
                enabled: true,
                radius: 3,
                symbol: 'circle'
              }
            }
          ]
    };

    this.chartOptions1.chart!.events = {
      load: this.chartCallback1
    };
    this.updateFlag = true;
  }

  filterByDateSleepGrpah(startDateInput: HTMLInputElement, endDateInput: HTMLInputElement): void {
    const start = startDateInput.value;
    const end = endDateInput.value;
    if (start && end) {
      this.startDate = start;
      this.endDate = end;

      // Show loading spinner
      this.isLoading = 1;

      // Fetch new data - zoom will be reset after data loads
      this.getSleepGraphData();
      this.getSleepData(start, end);
    }
  }

  setTimePeriod(period: string) {
    const today = new Date();
    let startDate: Date;

    switch (period) {
      case '1week':
        startDate = new Date(today.getTime() - (7 * 24 * 60 * 60 * 1000));
        break;
      case '2weeks':
        startDate = new Date(today.getTime() - (14 * 24 * 60 * 60 * 1000));
        break;
      case '1month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
        break;
      case '3months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
        break;
      default:
        startDate = new Date(today.getTime() - (7 * 24 * 60 * 60 * 1000));
    }

    // Update the date picker values
    this.previousDateBmi = startDate;
    this.currentDateBmi = today;

    // Format dates for API calls
    const startDateStr = formatDate(startDate, 'Y-MM-d', 'en');
    const endDateStr = formatDate(today, 'Y-MM-d', 'en');

    // Update internal date variables
    this.startDate = startDateStr;
    this.endDate = endDateStr;

    // Show loading spinner
    this.isLoading = 1;

    // Fetch sleep data for the selected period - zoom will be reset after data loads
    this.getSleepGraphData();
    this.getSleepData(startDateStr, endDateStr);
  }

  convertSecondsToDecimalHours(seconds: number): number {
    return +(seconds / 3600).toFixed(2);
  }

  // Helper function to calculate maximum value from percentage data arrays
  private getMaxValueFromPercentageData(): number | undefined {
    const allPercentageData = [
      ...this.deepSleepPerctGraph,
      ...this.remSleepPerctGraph,
      ...this.lightSleepPerctGraph,
      ...this.awakePerctGraph,
      ...this.totalSleepDurationPerct
    ].filter((value: number) => !isNaN(value) && value !== null);

    if (allPercentageData.length === 0) return undefined;

    const maxValue = Math.max(...allPercentageData);
    return Math.ceil(maxValue * 1.1);
  }

  // Helper function to calculate maximum value from hours data arrays
  private getMaxValueFromHoursData(): number | undefined {
    const allHoursData = this.totalSleepDurationHrs.filter((value: number | null) => value !== null && !isNaN(value as number)) as number[];

    // Include goal values in max calculation
    const goalValues: number[] = [];
    if (this.sleepGoals && this.sleepGoals.length > 0) {
      this.sleepGoals.forEach((goal: any) => {
        const goalHours = this.convertSecondsToDecimalHours(goal.goal);
        if (!isNaN(goalHours) && goalHours > 0) {
          goalValues.push(goalHours);
        }
      });
    }

    const allValues = [...allHoursData, ...goalValues];
    if (allValues.length === 0) return undefined;

    const maxValue = Math.max(...allValues);
    return Math.ceil(maxValue * 1.1);
  }

  // New API plotting helpers for Total Sleep Time chart (seconds on y-axis)
  private buildGoalPointsFromApiDays(): Highcharts.PointOptionsObject[] {
    const days = this.filteredTotalSleepDays || [];
    const points: Highcharts.PointOptionsObject[] = [];

    days.forEach((day: any) => {
      const date = (day?.date || '').toString();
      if (!date) return;
      if (day?.goal == null || day?.goal === undefined) return;

      const goalSeconds = Number(day?.goal);
      if (!Number.isFinite(goalSeconds)) return;

      // Support both "2026-01-15" and "2026-01-15T00:00:00"; goal always at midnight
      const x = moment(date).startOf('day').valueOf();
      if (!Number.isFinite(x)) return;
      points.push({
        x,
        y: this.convertSecondsToDecimalHours(goalSeconds),
        custom: {
          dateStr: moment(x).format('ddd, MMM D, YYYY'),
          seconds: goalSeconds
        }
      });
    });

    // Sort by time so the line draws correctly
    points.sort((a: any, b: any) => (a.x as number) - (b.x as number));
    return points;
  }

  private buildTotalSleepPointsFromApiDays(): Highcharts.PointOptionsObject[] {
    const days = this.filteredTotalSleepDays || [];
    const points: Highcharts.PointOptionsObject[] = [];

    days.forEach((day: any) => {
      const data = day?.data;
      if (!data) return;

      const durationSeconds = Number(data?.duration);
      const endTime = data?.endTime;
      if (!Number.isFinite(durationSeconds) || !endTime) return;

      const endTs = moment(endTime).valueOf();
      if (!Number.isFinite(endTs)) return;

      points.push({
        x: endTs,
        y: this.convertSecondsToDecimalHours(durationSeconds),
        custom: {
          dateStr: moment(endTs).format('ddd, MMM D, YYYY'),
          seconds: durationSeconds,
          info1: data?.startTime ? formatDate(data.startTime, 'hh:mm a', 'en') : '',
          info2: data?.endTime ? formatDate(data.endTime, 'hh:mm a', 'en') : ''
        }
      });
    });

    points.sort((a: any, b: any) => (a.x as number) - (b.x as number));
    return points;
  }

  private getMaxValueFromHoursSeriesData(): number {
    const goalPoints = this.buildGoalPointsFromApiDays();
    const totalPoints = this.buildTotalSleepPointsFromApiDays();
    const values = [...goalPoints, ...totalPoints]
      .map((p: any) => p?.y)
      .filter((v: any) => Number.isFinite(v)) as number[];

    if (values.length === 0) return 12; // default: 12 hours
    const maxVal = Math.max(...values);
    return Math.ceil(maxVal * 1.1);
  }

  // Chart callback methods for zoom functionality
  private chartCallback1 = (chart: any) => {
    const actualChart = chart.target;
    this.chartInstance1 = actualChart;
  }

  private chartCallback2 = (chart: any) => {
    const actualChart = chart.target;
    this.chartInstance2 = actualChart;
  }

  // Reset chart zoom methods
  private resetChartZoom1(): void {
    const chart = this.getChartInstance1();
    if (chart && chart.xAxis && chart.xAxis[0] && typeof chart.xAxis[0].setExtremes === 'function') {
      chart.xAxis[0].setExtremes(null, null);
    }
  }

  private resetChartZoom2(): void {
    const chart = this.getChartInstance2();
    if (chart && chart.xAxis && chart.xAxis[0] && typeof chart.xAxis[0].setExtremes === 'function') {
      chart.xAxis[0].setExtremes(null, null);
    }
  }

  // Get chart instances
  private getChartInstance1(): any {
    if (this.chartInstance1) {
      return this.chartInstance1;
    }

    if (this.chart1 && this.chart1.chart) {
      this.chartInstance1 = this.chart1.chart;
      return this.chartInstance1;
    }

    return null;
  }

  private getChartInstance2(): any {
    if (this.chartInstance2) {
      return this.chartInstance2;
    }

    if (this.chart2 && this.chart2.chart) {
      this.chartInstance2 = this.chart2.chart;
      return this.chartInstance2;
    }

    return null;
  }

  // Force chart recreation when data changes
  private forceChartRecreation(): void {
    // Destroy existing chart instances if they exist
    if (this.chartInstance1) {
      this.chartInstance1.destroy();
      this.chartInstance1 = null;
    }
    if (this.chartInstance2) {
      this.chartInstance2.destroy();
      this.chartInstance2 = null;
    }

    // Hide and show charts to force recreation
    this.showChart1 = false;
    this.showChart2 = false;
    this.updateFlag = false;

    // Use longer delay to ensure complete destruction
    setTimeout(() => {
      this.showChart1 = true;
      this.showChart2 = true;
      this.updateFlag = true;
    }, 100);
  }

  // Create empty charts with proper structure and message
  createEmptyCharts(): void {
    // Reset chart instances to ensure clean recreation
    this.chartInstance1 = null;
    this.chartInstance2 = null;

    // Force chart recreation
    this.forceChartRecreation();

    // Small delay to ensure charts are recreated before rendering empty data
    setTimeout(() => {
      this.createEmptyChart();
    }, 150);
  }

  // Create empty chart with message in plotting area
  createEmptyChart(): void {
    this.chartOptions2 = {
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line'
      },
      title: { text: 'Sleep Stage Percentages' },
      subtitle: {
        text: 'No data recorded for the selected date range',
        align: 'center',
        verticalAlign: 'middle',
        style: { color: '#888', fontSize: '14px' }
      },
      xAxis: {
        type: 'datetime',
        categories: [],
        lineWidth: 2,
        labels: { enabled: true }
      },
      credits: { enabled: false },
      yAxis: {
        min: 0,
        max: 100,
        lineWidth: 2,
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        labels: { enabled: false },
        tickWidth: 0,
        tickLength: 0,
        title: {
          text: 'Percentage for a day'
        }
      },
      tooltip: {
        enabled: false
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: false
          },
          enableMouseTracking: false
        }
      },
      series: [
        {
          name: 'Deep Sleep',
          type: 'line',
          data: [],
          showInLegend: true,
          color: '#66CC66'
        },
        {
          name: 'REM Sleep',
          type: 'line',
          data: [],
          showInLegend: true,
          color: '#21A4B6'
        },
        {
          name: 'Light Sleep',
          type: 'line',
          data: [],
          showInLegend: true,
          color: '#4787F6'
        },
        {
          name: 'Awake Time',
          type: 'line',
          data: [],
          showInLegend: true,
          color: '#FFDA48'
        }
      ]
    };

    this.chartOptions1 = {
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line'
      },
      title: { text: 'Total Sleep Time' },
      subtitle: {
        text: 'No data recorded for the selected date range',
        align: 'center',
        verticalAlign: 'middle',
        style: { color: '#888', fontSize: '14px' }
      },
      xAxis: [{
        type: 'datetime',
        categories: [],
        lineWidth: 2,
        labels: { enabled: true },
        plotBands: []
      }],
      credits: { enabled: false },
      yAxis: {
        min: 0,
        max: 12,
        lineWidth: 2,
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        labels: { enabled: false },
        tickWidth: 0,
        tickLength: 0,
        title: {
          text: 'Hours'
        }
      },
      tooltip: {
        enabled: false
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: false
          },
          enableMouseTracking: false
        }
      },
      series: [
        {
          name: 'Total sleep',
          type: 'line',
          data: [],
          showInLegend: true,
          color: '#66CC66'
        }
      ]
    };
  }

  // Fetch sleep data for the bar chart
  getSleepData(startDate: any, endDate: any) {
    this.dashboardService.getSleepData(this.memberId, startDate, endDate).subscribe(response => {
      this.sleep = response;
      this.sleep = this.sleep.result;
      // Update the bar chart with new data
      this.updateSleepBarChart();
    });
  }
}
